// The casino's small markup pieces (casino.md, ECONOMY · CASINO).
//
// Four game screens draw the same things: a playing card, a row of them,
// a control and the name of a seat. The pieces live here one time, so the
// screens read the same and a change reaches all of them together.
//
// The module touches no DOM and holds no state. Every function takes plain
// values and gives back one string, so a test reads the markup without a
// document.
//
// **A card is text, and a suit is never an emoji.** ♠ ♥ ♦ ♣ carry an emoji
// form, and iOS draws the heart and the diamond as a picture. A red picture
// beside a black glyph is not a hand of cards. `textGlyph` appends U+FE0E,
// which asks for the text form and is inert on every other platform.
//
// **The wire byte is the server's `casino.Card`** (`server/internal/casino/
// cards.go`): the rank is bits 0 to 3, 1 for an ace through 13 for a king,
// and the suit is bits 4 and 5 in the order clubs, diamonds, hearts,
// spades. A byte of 0 is no card at all, which is what a face-down hole
// card looks like before the reveal.

import type { CasinoSeatView } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { esc } from '../esc';
import { textGlyph } from '../glyph';
import { pillGroup } from '../choice/choice';

/** The rank glyphs, indexed by the rank the wire states. Index 0 is no card. */
const RANK_GLYPH = ['', 'A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

/** The suit glyphs, in the wire's own suit order. */
const SUIT_GLYPH = ['♣', '♦', '♥', '♠'];

/** The two red suits, as the suit numbers the wire uses. */
const SUIT_DIAMONDS = 1;
const SUIT_HEARTS = 2;

/** The rank of one card byte: 1 for an ace, 13 for a king, 0 for no card. */
export function cardRank(c: number): number {
  return c & 0x0f;
}

/** The suit of one card byte: 0 clubs, 1 diamonds, 2 hearts, 3 spades. */
export function cardSuit(c: number): number {
  return (c >> 4) & 0x03;
}

/** True for a diamond and for a heart, which the sheet paints red. */
export function cardIsRed(c: number): boolean {
  const suit = cardSuit(c);
  return suit === SUIT_DIAMONDS || suit === SUIT_HEARTS;
}

/** True while the byte names a real card. A 0 byte names none. */
export function cardValid(c: number): boolean {
  const rank = cardRank(c);
  return rank >= 1 && rank <= 13;
}

/**
 * One card as plain text, such as `A♠︎`.
 *
 * A sentence that names a card takes this, because a placeholder holds
 * text and never markup. A 0 byte names no card, so the answer is empty.
 */
export function cardText(c: number): string {
  if (!cardValid(c)) return '';
  return `${RANK_GLYPH[cardRank(c)]}${textGlyph(SUIT_GLYPH[cardSuit(c)])}`;
}

/** How a screen asks for one card. */
export interface CardOpts {
  /** Draw the back. The dealer's hole card and another seat's hand. */
  hidden?: boolean;
  /** The reader's own hand, which is drawn larger. */
  big?: boolean;
  /**
   * A 0 byte is an EMPTY PLACE and not a back.
   *
   * The hold'em board holds five places from the first frame, so the row
   * never moves under the reader's finger as the cards arrive. A back
   * there would say the house holds a card it does not hold.
   */
  slot?: boolean;
}

/**
 * One playing card as markup.
 *
 * A hidden card and a 0 byte both give the back, because the server sends
 * a 0 for a card this reader must not see. The screen therefore states
 * what it wants and never reads a card it was not given. A screen that
 * asks for `slot` gets the empty place for a 0 byte instead.
 */
export function card(c: number, opts: CardOpts = {}): string {
  const big = opts.big ? ' big' : '';
  if (opts.slot && !opts.hidden && !cardValid(c)) {
    return `<span class="card slot${big}"></span>`;
  }
  if (opts.hidden || !cardValid(c)) return `<span class="card back${big}"></span>`;
  const paint = cardIsRed(c) ? 'red' : 'blk';
  return `<span class="card ${paint}${big}">${RANK_GLYPH[cardRank(c)]}`
    + `<i>${textGlyph(SUIT_GLYPH[cardSuit(c)])}</i></span>`;
}

/** A row of cards, in the order the wire gave them. */
export function cards(list: readonly number[], opts: CardOpts = {}): string {
  return list.map((c) => card(c, opts)).join('');
}

/**
 * One control of a screen: a button `Overlay.actButton` routes by its
 * `data-op`. `on` false greys it out, `cls` is its class list and `extra`
 * every other attribute it carries. The order is fixed — the class, the
 * two routing attributes, the extras, then `disabled` — so every screen
 * writes one shape and a test reads one.
 */
export function ctl(op: string, label: string, on = true, cls = '', extra = ''): string {
  return `<button${cls === '' ? '' : ` class="${cls}"`} data-act="casino"`
    + ` data-op="${op}"${extra}${on ? '' : ' disabled'}>${label}</button>`;
}

/**
 * A control behind the two-press gate (R837). `Overlay.armKey` builds the
 * key from `data-act` and `data-id`, so the button carries an id no other
 * armed control of its screen uses, and `data-arm="1"` tells the overlay
 * to arm it. `armed` puts on the class the sheet paints.
 */
export function armedCtl(
  op: string, id: string | number, label: string, armed: boolean, cls: string, extra = '',
): string {
  return `<button class="${cls}${armed ? ' armed' : ''}" data-act="casino" data-op="${op}"`
    + ` data-id="${id}" data-arm="1"${extra}>${label}</button>`;
}

/** One stake of a stake row. */
export interface StakeChoice {
  /** The id of the pill, unique in its row and across the rows of one
   *  screen, because the kit keys the slide of its fill by the ids. */
  id: string;
  /** The price the pill states. */
  label: string;
  /** False when the reader cannot pay the stake. */
  payable: boolean;
  /** The attributes the panel's dispatcher reads: `op` and the stake. */
  data: Readonly<Record<string, string>>;
}

/**
 * One stake row: the lobby's tier pick, the machine's stake and the stake
 * of a blackjack bet (R2118, ui-map-interface.md §18). It is ONE pill
 * group of the choice kit, a radio group with the pick checked, and each
 * press keeps its `data-act="casino"`, its `data-op` and its stake, so the
 * panel routes it as before. A stake the reader cannot pay is shut and
 * names its reason as its title; `why` adds the visible line under the row
 * for a finger, which reads no title.
 */
export function stakeRow(list: readonly StakeChoice[], chosen: string, why: boolean): string {
  const group = pillGroup(list.map((s) => ({
    id: s.id, label: s.label, data: s.data,
    ...(s.payable ? {} : { locked: true, title: S.casino_stake_short }),
  })), chosen, 'casino', { label: S.casino_stake_label });
  const shut = why && list.some((s) => !s.payable);
  return group + (shut ? `<div class="short">${S.casino_stakes_shut}</div>` : '');
}

/**
 * The name one seat wears.
 *
 * An autopilot seat and a fictive seat read as a player, and the owner
 * ruled that neither carries a tag. A name is player text, so it is
 * escaped. A seat the server gave no name at all reads as its number.
 */
export function seatName(seat: CasinoSeatView): string {
  const name = seat.name.trim();
  return name === '' ? S.casino_seat_n({ n: seat.seat }) : esc(name);
}

/**
 * The seat's own attributes for its element.
 *
 * The two flags stay readable here and nowhere else: a screen that wants
 * to know what sits at a seat reads the attribute, and no reader sees a
 * tag beside the name.
 */
export function seatData(seat: CasinoSeatView): string {
  return ` data-seat="${seat.seat}"`
    + (seat.fictive ? ' data-fictive="1"' : '')
    + (seat.bot ? ' data-bot="1"' : '');
}
